import { useEffect, useRef, useState } from 'react';
import {
  ehAppEmbutido,
  ehAppInstalado,
  ehIOS,
  ehAndroid,
  ehInstagram,
  nomeDoApp,
} from '@/lib/navegador';

const CHAVE_FECHADA = 'seliga:faixa_navegador_fechada';

function jaFechada(): boolean {
  try {
    return sessionStorage.getItem(CHAVE_FECHADA) === '1';
  } catch {
    return false;
  }
}

/**
 * Faixa no topo, exibida só dentro de webviews (Instagram, Facebook, TikTok
 * etc.). SUGERE abrir no navegador de verdade — e sempre tem "Continuar aqui",
 * que fecha a faixa e deixa a pessoa seguir exatamente onde estava.
 */
export default function FaixaNavegador() {
  const [visivel, setVisivel] = useState(false);
  const [fechada, setFechada] = useState(() => jaFechada());
  const [planoB, setPlanoB] = useState(false);
  const [copiado, setCopiado] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Só aparece em webview de app, e nunca quando já é um PWA instalado.
  useEffect(() => {
    if (ehAppEmbutido && !ehAppInstalado) setVisivel(true);
  }, []);

  // Como a faixa é fixa, empurra o conteúdo do site para baixo com padding-top
  // proporcional à altura real da faixa (que pode crescer com o plano B).
  useEffect(() => {
    if (!visivel || fechada) {
      document.body.style.paddingTop = '0px';
      return;
    }
    const el = ref.current;
    if (!el) return;
    const atualizar = () => {
      document.body.style.paddingTop = `${el.offsetHeight}px`;
    };
    atualizar();
    const ro = new ResizeObserver(atualizar);
    ro.observe(el);
    return () => {
      ro.disconnect();
      document.body.style.paddingTop = '0px';
    };
  }, [visivel, fechada]);

  function continuarAqui() {
    try {
      sessionStorage.setItem(CHAVE_FECHADA, '1');
    } catch {
      // armazenamento indisponível: fecha só nesta tela
    }
    setFechada(true);
  }

  const copiarLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopiado(true);
      window.setTimeout(() => setCopiado(false), 3000);
    } catch {
      // Clipboard indisponível (http sem HTTPS): não faz nada.
    }
  };

  const abrirNoNavegador = () => {
    const url = window.location.href;

    if (ehAndroid) {
      // Android: esquema intent com fallback para quem não tem Chrome.
      const semEsquema = url.replace(/^https?:\/\//, '');
      window.location.href =
        'intent://' +
        semEsquema +
        '#Intent;scheme=https;package=com.android.chrome' +
        ';S.browser_fallback_url=' +
        encodeURIComponent(url) +
        ';end';
    } else if (ehIOS && ehInstagram) {
      // iOS dentro do Instagram: só funciona por <meta refresh>.
      const m = document.createElement('meta');
      m.httpEquiv = 'refresh';
      m.content = '0;url=instagram://extbrowser/?url=' + encodeURIComponent(url);
      document.head.appendChild(m);
    } else if (ehIOS) {
      // iOS em outros apps: abre no Safari.
      window.location.href = 'x-safari-' + url;
    } else {
      window.open(url, '_blank');
    }

    // Plano B: se 1,5s depois a página ainda estiver aberta (não navegou),
    // é sinal de que não funcionou — mostra o passo a passo manual.
    window.setTimeout(() => setPlanoB(true), 1500);
  };

  if (!visivel || fechada) return null;

  return (
    <div ref={ref} className="fixed inset-x-0 top-0 z-[100] bg-primary-700">
      <div className="mx-auto flex w-full max-w-[1400px] items-center justify-between gap-3 px-4 py-2.5">
        <p className="min-w-0 flex-1 text-xs font-medium leading-snug text-white/90">
          Você está no navegador do {nomeDoApp()}.
          <span className="mt-0.5 block text-[11px] font-normal text-white/70">
            Aqui as senhas salvas não aparecem.
          </span>
        </p>
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={abrirNoNavegador}
            className="cursor-pointer whitespace-nowrap rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-primary-700 transition-opacity hover:opacity-90"
          >
            Abrir no navegador
          </button>
          <button
            type="button"
            onClick={continuarAqui}
            className="cursor-pointer whitespace-nowrap rounded-full px-2 py-1.5 text-xs font-semibold text-white/80 transition-colors hover:bg-white/10 hover:text-white"
          >
            Continuar aqui
          </button>
        </div>
      </div>

      {/* Plano B — passo a passo manual + copiar link. */}
      {planoB && (
        <div className="border-t border-white/20 bg-primary-800 px-4 py-4">
          <div className="mx-auto w-full max-w-[1400px]">
            <p className="text-sm font-semibold text-white">Como abrir no navegador</p>
            <p className="mt-2 text-sm leading-relaxed text-white/90">
              {ehIOS
                ? 'Toque nos três pontinhos (•••) no canto de cima e escolha "Abrir no navegador".'
                : 'Toque nos três pontinhos (⋮) no canto de cima e escolha "Abrir no Chrome".'}
            </p>

            <button
              type="button"
              onClick={copiarLink}
              className="mt-3 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full bg-white px-4 py-2 text-sm font-semibold text-primary-700 transition-opacity hover:opacity-90"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className={copiado ? 'ri-check-line' : 'ri-file-copy-line'} />
              </span>
              {copiado ? 'Link copiado! Cole no seu navegador.' : 'Copiar o link'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}